import {
  brandName,
  environmentFromCookie,
  environmentLabel,
  type Environment,
} from "../../api/src/shared/brand.js";
import { el } from "./dom.js";

/**
 * Which environment this page is running in, and what that changes (g93).
 *
 * One build runs everywhere and `index.html` is the same in every
 * environment, so the page cannot be told by its own document. The web server
 * sets a cookie on test and never in production, and this reads it: before
 * the first paint, on the first visit, and with no network. Nothing here
 * compares a hostname, and nothing asks the API, which a signed-out or
 * offline page could not reach.
 *
 * Read on every call rather than kept: a read is a string split, and a kept
 * value would be one more thing for a test to reset.
 */
export function environment(): Environment {
  try {
    return environmentFromCookie(document.cookie);
  } catch {
    // A sandboxed document throws on `document.cookie`. Production is the
    // answer that changes nothing.
    return "production";
  }
}

/** "Trayflux", or "Trayflux Test". The one place a tab title takes its brand from. */
export function brand(): string {
  return brandName(environment());
}

/** A tab title: what the page is about, then the brand. `Groceries - Trayflux`. */
export function titled(context?: string): string {
  return context === undefined ? brand() : `${context} - ${brand()}`;
}

/** Where a chip sits, which is all that differs between them: see `.environment-chip`. */
export type ChipPlace = "wordmark" | "nav" | "topbar" | "page";

/**
 * The "Test" chip as the children to add: one on test, and none in
 * production, where nothing is drawn at all. A list so a caller spreads it
 * into its children and has no production branch of its own to get wrong.
 * Text, so a screen reader reads it where it stands: beside the wordmark it
 * makes the link's name "Trayflux Test".
 */
export function environmentChip(place: ChipPlace): HTMLElement[] {
  const label = environmentLabel(environment());
  if (label === null) return [];
  return [el("span", { class: `environment-chip environment-chip-${place}`, text: label })];
}
